import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import MoodSelector from './MoodSelector';
import MoodGraph from './MoodGraph';
import { moodService } from '../../../services/mood.service';
import { useAuth } from '../../../context/AuthContext';
import './MoodTracker.css';

const MOODS = {
  GREAT: { value: 5, emoji: '😄', label: 'Great' },
  GOOD: { value: 4, emoji: '🙂', label: 'Good' },
  OKAY: { value: 3, emoji: '😐', label: 'Okay' },
  BAD: { value: 2, emoji: '😕', label: 'Bad' },
  AWFUL: { value: 1, emoji: '😢', label: 'Awful' }
};

const MoodTracker = () => {
  const [todaysMood, setTodaysMood] = useState(null);
  const [moodHistory, setMoodHistory] = useState([]);
  const { user } = useAuth();
  const today = format(new Date(), 'yyyy-MM-dd');

  useEffect(() => {
    let unsubscribe;

    if (user) {
      unsubscribe = moodService.subscribeMoods(user.uid, (moods) => {
        setMoodHistory(moods);
        const todayMood = moods.find(mood => mood.date === today);
        setTodaysMood(todayMood);
      });
    }

    return () => unsubscribe && unsubscribe();
  }, [user, today]);

  const handleMoodSelect = async (mood) => {
    if (todaysMood) {
      // If mood already selected for today, don't allow another selection
      return;
    }

    try {
      // Optimistic update for immediate UI feedback
      const newMood = {
        id: Date.now().toString(),
        userId: user.uid,
        mood: mood.value,
        emoji: mood.emoji,
        label: mood.label,
        date: today,
        createdAt: new Date()
      };

      // Update local state immediately
      setTodaysMood(newMood);
      setMoodHistory(prev => [...prev, newMood]);

      // Then update Firebase
      await moodService.recordMood(user.uid, {
        mood: mood.value,
        emoji: mood.emoji,
        label: mood.label,
        date: today
      });
    } catch (error) {
      console.error('Error recording mood:', error);
      // Revert optimistic update on error
      setTodaysMood(null);
      setMoodHistory(prev => prev.filter(m => m.id !== Date.now().toString()));
      // Handle error (show notification, etc.)
    }
  };

  const getTodayMessage = () => {
    if (todaysMood) {
      return (
        <div className="mood-message">
          <p className="mood-status">
            <span className="mood-emoji">{todaysMood.emoji}</span>
            Today's mood: {todaysMood.label}
          </p>
          <p className="mood-note">
            Come back tomorrow to track your next mood!
          </p>
        </div>
      );
    }
    return "How are you feeling today?";
  };

  return (
    <motion.div 
      className="mood-tracker"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="mood-tracker-header">
        <h2>Mood Tracker</h2>
        <div className="mood-tracker-subheader">
          {getTodayMessage()}
        </div>
      </div>

      {!todaysMood && (
        <MoodSelector 
          moods={MOODS} 
          onSelect={handleMoodSelect}
        />
      )}

      <MoodGraph 
        moodHistory={moodHistory}
        moods={MOODS}
        todaysMood={todaysMood}
      />
    </motion.div>
  );
};

export default MoodTracker; 